import { env } from '@env'
import { cmsApi } from '@zet/cms-network'
import ENDPOINTS, { APP_NAME } from '@zet/end-points'
import { zetApi } from '@zet/network'
import storageUtil from '@zet/utils/storage'
import _ from 'lodash'

import {
  BannerResponse,
  BrandCategoryData,
  CBMPRoductResponse,
  ExtraPayoutPercentage,
  GetCriterialIndexesRequest,
  IBestBrands,
  IBrandComsData,
  IBrandShareRequestParams,
  ICustomerForPartnerId,
  IGetCriterialIndexesResponse,
  IGetRecommendationPartnerSpecific,
  IPincode,
  IRecommendationFilterPayload,
  IRecommendationListResponse,
  IResponseData,
  IShareLink,
  IWebinars,
  IZetStoreResponse,
  LoyaltyGifts,
  LoyaltyTierDetails,
  MyTeamsBransPayout,
  ZetStoreResponse,
} from './types'

export const getPincodeCheck = async (pincodeData: IPincode) => {
  const { pincode, categoryId } = pincodeData
  const { data } = await zetApi.request({
    url: ENDPOINTS[APP_NAME].getPincodeCheck.endpoint,
    method: ENDPOINTS[APP_NAME].getPincodeCheck.method,
    params: {
      pincode,
      categoryId,
    },
  })
  return data?.data
}

export const getBrandsCategory = async () => {
  const { data } = await zetApi.request<BrandCategoryData>({
    url: ENDPOINTS[APP_NAME].getBrandsCategory.endpoint,
    method: ENDPOINTS[APP_NAME].getBrandsCategory.method,
  })
  return data?.data
}

export const getBrandDataByPartnerId = async (
  partnerId: string | number,
  locale: string
) => {
  storageUtil.deleteObject(`brandcomm-version-${partnerId}-${locale}-v1`)
  storageUtil.deleteObject(`brandcomm-content-${partnerId}-${locale}-v1`)

  const brandCommVersion = storageUtil.getObject(
    `brandcomm-version-${partnerId}-${locale}-v2`
  )

  try {
    if (!partnerId) throw new Error('Empty Partner Id')
    const { data } = await zetApi.request({
      url: ENDPOINTS[APP_NAME].getBrandDataByPartnerId.endpoint,
      method: ENDPOINTS[APP_NAME].getBrandDataByPartnerId.method,
      data: {
        [partnerId]: brandCommVersion === 221 ? null : brandCommVersion,
      },
    })

    if (_.isEmpty(data?.data[partnerId])) {
      return storageUtil.getObject(
        `brandcomm-content-${partnerId}-${locale}-v2`
      ) as IBrandComsData
    }
    const response: any = {}

    let subAgentContent
    try {
      subAgentContent = JSON.parse(data?.data[partnerId]?.subAgentContent)
    } catch (e) {
      /* empty */
    }

    const content = JSON.parse(data?.data[partnerId].content)
    response[partnerId] = {
      version: data?.data[partnerId].version,
      name: content?.name || '',
      topFeatures: content?.specifications,
      marketingResources: content?.sharingMaterial,
      shareContent: content?.shareContent,
      logo: content?.logoUrl,
      payoutDetails: content?.payoutDetails,
      brandBenefits: content?.benefits,
      importantTermsAndConditions: content?.termsAndConditions,
      applicationProcess: content?.applicationProcess,
      eligibilityCriteria: content?.eligibilityCriteria,
      helpAndSupportFaqs: content?.helpAndSupportFaqs,
      shareLinkImage: content?.CategoryCreditcard?.cardImage,
      commission: content?.commissionContent,
      amount: content?.amount,
      categoryId: content?.categoryId,
      subCategory: content?.subCategory,
      rejectionAmount: content?.rejectionPayout?.value,
      categoryCommissionShort: content?.categoryCommissionShort,
      leadFlow: content?.leadFlow,
      cardBackground: content?.cardBackground,
      isNewBrand: data?.data[partnerId]?.isNewBrand,
      isPayoutChanged: data?.data[partnerId]?.isPayoutChanged,
      ARN: content?.ARN,
      shareLinkConfirmation: content?.shareLinkConfirmation,
      colorGradient: data?.data[partnerId]?.colorGradient,
      highGuaranteedContent: content?.preApprovedContent,
      brandSpecification: content?.brandSpecification,
      leadVideoUrl: content?.leadVideoUrl,
      productCardLink: content?.productCardLink,
      subAgentContent,
      ...(content.recommendationSbmContext && {
        recommendationSbmContext: content.recommendationSbmContext,
      }),
    }
    storageUtil.saveObject(
      `brandcomm-version-${partnerId}-${locale}-v2`,
      response[partnerId]?.version
    )
    storageUtil.saveObject(
      `brandcomm-content-${partnerId}-${locale}-v2`,
      response[partnerId]
    )
    return response[partnerId] as IBrandComsData
  } catch (e) {
    console.log('error fetch brand data')
    throw e
  }
}

export const getShareBrandURL = async ({
  partnerId,
  customerId,
  reopen = false,
  leadSource = 'appBrand',
  isEligibleBrand = true,
  uiRankOfPartner,
  brandSpecificPartnerId,
  brandCategory,
}: IBrandShareRequestParams) => {
  const { data } = await zetApi.request<IShareLink>({
    url: 'integration/customer/generateLink/v3',
    method: 'POST',
    params: {
      ...(partnerId ? { partnerId } : {}),
      ...(customerId ? { customerId } : {}),
      ...(leadSource ? { leadSource } : {}),
      ...(reopen ? { reopen } : {}),
      ...(isEligibleBrand ? { isEligibleBrand } : {}),
      ...(uiRankOfPartner ? { uiRankOfPartner } : {}),
      ...(brandSpecificPartnerId ? { brandSpecificPartnerId } : {}),
      brandCategory,
    },
  })
  return data?.data
}

export const getShareBrandURLV4 = async ({
  partnerId,
  customerId,
  reopen = false,
  leadSource = 'appBrand',
  isEligibleBrand = true,
  uiRankOfPartner,
  brandSpecificPartnerId,
  variantId,
  brandCategory,
}: IBrandShareRequestParams) => {
  const { data } = await zetApi.request<IShareLink>({
    url: 'integration/customer/generateLink/v4',
    method: 'POST',
    params: {
      ...(partnerId ? { partnerId } : {}),
      ...(customerId ? { customerId } : {}),
      ...(leadSource ? { leadSource } : {}),
      ...(reopen ? { reopen } : {}),
      ...(isEligibleBrand ? { isEligibleBrand } : {}),
      ...(uiRankOfPartner ? { uiRankOfPartner } : {}),
      ...(brandSpecificPartnerId
        ? { leadSourcePartnerId: brandSpecificPartnerId }
        : {}),
      variantId,
      brandCategory,
    },
  })

  return data?.data
}

export const getPartnerIdsV2 = async (categoryId: number) => {
  const { data } = await zetApi.request<BrandCategoryData>({
    url:
      ENDPOINTS[APP_NAME].getPartnersDataByCategoryId.endpoint +
      `/${categoryId}`,
    method: ENDPOINTS[APP_NAME].getPartnersDataByCategoryId.method,
  })

  return data?.data
}

export const getAgentDashboardForBrands = async (partnerId: number) => {
  const { data } = await zetApi.request({
    url: ENDPOINTS[APP_NAME].fetchDashboard.endpoint,
    method: ENDPOINTS[APP_NAME].fetchDashboard.method,
    params: { partnerId: partnerId },
  })
  return data?.data
}

export const getGlobalShareBrandURL = async (inputData: {
  partnerId: number
  customerAdditionSource: string
  variantId?: string
  leadSource?: string
}) => {
  const { data } = await zetApi.get<IShareLink>(
    `customer/generateShareLink/v2?linkType=partnerShareLink&partnerId=${
      inputData?.partnerId
    }&customerAdditionSource=${inputData?.customerAdditionSource}${
      inputData?.variantId ? `&variantId=${inputData.variantId}` : ''
    }${inputData?.leadSource ? `&leadSource=${inputData.leadSource}` : ''}`
  )
  return data?.data
}

export const getShareCreative = async (partnerId: number, language: string) => {
  const { data } = await zetApi.get(
    `user/generateCreative/v2?partnerId=${partnerId}&language=${language}`
  )

  return data?.data
}

export const getRecommendation = async (
  customerId: number,
  partnerId?: number,
  isDemoSale?: boolean
) => {
  const { data } = await zetApi.get<IRecommendationListResponse>(
    'customer/getRecommendation/v2',
    {
      params: {
        ...(customerId ? { customerId } : {}),
        ...(partnerId ? { partnerIdForSortingCategory: partnerId } : {}),
        ...(isDemoSale ? { isDemoSale } : {}),
      },
    }
  )
  if (data?.error?.code === 2022) {
    throw new Error(`API request failed with error: ${data?.error}`)
  }
  return data?.data
}

export const getCustomerByPartnerId = async (
  partnerId: number,
  searchText: string,
  offset: number
) => {
  const { data } = await zetApi.get<ICustomerForPartnerId>(
    `customer/getList/v2?status=eligible&partnerId=${partnerId}&limit=10&offset=${
      offset ?? 0
    }&searchString=${searchText}`
  )
  return { nextOffset: offset + 10, ...data?.data }
}

export const getFilteredRecommendationCategorySpecific = async ({
  customerId,
  categoryId,
  type,
}: IRecommendationFilterPayload) => {
  const { data } = await zetApi.get<IRecommendationListResponse>(
    'customer/getRecommendation/v2',
    {
      params: {
        ...(categoryId ? { categoryId } : {}),
        ...(customerId ? { customerId } : {}),
        ...(type ? { filterType: type } : {}),
      },
    }
  )
  return data?.data
}

export const getFilteredRecommendationPartnerSpecific = async ({
  customerId,
  partnerId,
}: IRecommendationFilterPayload) => {
  const { data } = await zetApi.get<IGetRecommendationPartnerSpecific>(
    'customer/getRecommendation/v2',
    {
      params: {
        ...(partnerId ? { partnerId } : {}),
        ...(customerId ? { customerId } : {}),
      },
    }
  )
  return data?.data
}

export const getTrainingForBrands = async (partnerId: number) => {
  const { data } = await zetApi.get<IWebinars>(
    `training/getTrainings/v2?type=upcoming&partnerId=${partnerId}`
  )
  return data.data
}

export async function getBestBrands() {
  const { data } = await zetApi.get<IBestBrands>('user/getAgentBestBrands')
  return data?.data
}

export async function getMagnetBenefits() {
  const { data } = await zetApi.get(`getBrandComms/v6?partnerId=193`)
  let content = {}
  if (data?.data?.content) {
    content = JSON.parse(data?.data?.content)
  }
  return content
}

export const getCreditBuilderBenefits = async () => {
  const { data } = await zetApi.get(
    `getBrandComms/v6?partnerId=${env?.ENV === 'PRODUCTION' ? 419 : 661}`
  )

  let content = {}
  if (data?.data?.content) {
    content = JSON.parse(data?.data?.content)
  }
  return content
}

export async function getExtraPayoutPercentage(id: string | number) {
  const { data } = await zetApi.get<ExtraPayoutPercentage>(
    `/loyalty/${id}/extra-payout-percentage`
  )
  return data?.data
}

export async function getLoyaltyTierDetails() {
  const { data } = await zetApi.get<LoyaltyTierDetails>(`/loyalty/tier-details`)
  return data?.data
}

export async function getMyTeamsBrandPayoutDetails(id: number) {
  const { data } = await zetApi.get<MyTeamsBransPayout>(
    `/api/v1/sub-agents/me/brands/${id}/payout`
  )
  return data?.data
}
export async function getCriteriaIndexes(
  suppliedData: GetCriterialIndexesRequest
) {
  const { data } = await zetApi.get<IGetCriterialIndexesResponse>(
    `customer/getCriterialIndexes?locale=${suppliedData?.locale}`
  )
  return data
}

export async function getNextStateForProductFlow({
  customerPayload,
  userState,
}: {
  customerPayload: {
    customerId?: number | null | string
    partnerId?: number
    fullName?: string | null
    categoryId?: string
    phoneNumber?: number | string | null
    pinCode?: number | string | null
    customerIntentList?: string[]
    teamMemberCode?: number | undefined
    otp?: object
    annualIncome?: number | string
    employmentType?: number
    modeOfIncome?: number
    panNo?: string
    dob?: string
    secondaryPhoneNumber?: string | null
    email?: string | null
  }
  userState: string
}) {
  const { data } = await zetApi.post<IResponseData>(
    customerPayload?.categoryId
      ? `eventHandle/getNextStateAddCustomer?categoryId=${customerPayload?.categoryId}`
      : 'eventHandle/getNextStateAddCustomer',
    {
      customerPayload,
      userState,
    }
  )
  if (data?.data) {
    return data?.data
  } else {
    return data?.error
  }
}

export async function geBanners(screenName: string) {
  const { data } = await cmsApi.get<BannerResponse>(
    `cms/api/banners/${screenName}`,
    {
      headers: {
        Authorization: null,
      },
    }
  )
  return data?.data
}

export async function getLoyaltyGifts() {
  const { data } = await zetApi.get<LoyaltyGifts>(`/loyalty/v2/gifts`)
  return data?.data
}

export async function getCBMProductDetails() {
  const { data } = await cmsApi.get<CBMPRoductResponse>(`cms/api/product-cmb`, {
    headers: {
      Authorization: null,
    },
  })

  return data
}

export const getZetStore = async ({ partnerId }: { partnerId: number }) => {
  const { data } = await zetApi.get<ZetStoreResponse>(
    `getBrandComms/v6?partnerId=${partnerId}`
  )

  let content: IZetStoreResponse['content'] | object = {}

  if (data?.data?.content) {
    content =
      typeof data.data.content === 'string'
        ? (JSON.parse(data.data.content) as IZetStoreResponse['content'])
        : data.data.content
  }
  return content
}
